import { cn } from '@distribute/frontend/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { ControlPanel } from './ControlPanel';
import { PdfExtendedPreview } from './PdfExtendedPreview';

type Props = {
  mediaWidth?: number;
  mediaHeight?: number;
  align: string;
  fileUrl: string;
  fileName: string;
  origin: string;
  selected?: boolean;
  handleClick?: () => void;
  updateSize?: (width: number, height?: number) => void;
  editorWidth?: number;
};

export const PdfComponent = ({
  mediaWidth,
  mediaHeight = 0,
  align,
  fileUrl,
  fileName,
  origin,
  selected,
  handleClick,
  updateSize,
  editorWidth = 0,
}: Props) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [previewScale, setPreviewScale] = useState(1);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setPageNumber(1);
      setError(null);
    },
    []
  );

  const onDocumentLoadError = useCallback((err: Error) => {
    console.error('PDF load error:', err);
    setError('Failed to load PDF. Please try again.');
  }, []);

  const changePage = useCallback(
    (offset: number) => {
      setPageNumber((prev) =>
        Math.max(1, Math.min(prev + offset, numPages || 1))
      );
    },
    [numPages]
  );

  const handlePreviousClick = () => {
    changePage(-1);
  };

  const handleNextClick = () => {
    changePage(1);
  };

  const handleTogglePreview = () => {
    setIsPreviewOpen((prevIsOpen) => !prevIsOpen);
  };

  const downloadPDF = useCallback(() => {
    const link = document.createElement('a');
    link.target = '_blank';
    link.href = fileUrl;
    link.download = fileName || 'pdf';
    link.click();
  }, [fileUrl, fileName]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 2));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const zoomInPreview = useCallback(() => {
    setPreviewScale((prev) => Math.min(prev + 0.25, 2));
  }, []);

  const zoomOutPreview = useCallback(() => {
    setPreviewScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const ContentRef = useRef<HTMLDivElement | null>(null);

  const wrapperClass = cn(
    'relative group cursor-pointer z-10 max-w-full',
    selected && 'outline-4 outline-brand-500'
  );

  const flexWrapperClassName = cn(
    'flex items-center relative',
    align === 'left' && 'justify-start mt-7 w-full',
    align === 'center' && 'justify-center mt-7 w-full',
    align === 'right' && 'justify-end mt-7 w-full'
  );

  const [docWidth, setDocWidth] = useState(0);

  useEffect(() => {
    const current = ContentRef.current;
    if (!current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width - 40;
        setDocWidth(width);
      }
    });

    resizeObserver.observe(current);

    return () => {
      resizeObserver.unobserve(current);
      resizeObserver.disconnect();
    };
  }, []);

  const handleResizeFile = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>,
      direction: 'nw' | 'ne' | 'sw' | 'se' | 'right' | 'left',
      isOnlyHorizontal?: boolean
    ) => {
      e.stopPropagation();
      e.preventDefault();

      if (!updateSize) return;

      const startY = e.pageY;
      const startX = e.pageX;
      const initialRect = ContentRef.current?.getBoundingClientRect();

      if (!initialRect) return;

      const startHeight = initialRect.height;
      const startWidth = initialRect.width;

      const maxWidth =
        ContentRef.current?.closest('.node-column')?.getBoundingClientRect()
          .width || editorWidth;

      const maxHeight = document
        .getElementsByClassName('react-pdf__Page')[0]
        .getBoundingClientRect().height;

      const handleMouseMove = (e: MouseEvent) => {
        let newWidth = startWidth;
        let newHeight = startHeight;

        // Horizontal resize
        if (isOnlyHorizontal) {
          newWidth =
            direction === 'left'
              ? startWidth - (e.pageX - startX)
              : startWidth + (e.pageX - startX);

          newWidth = Math.max(Math.min(newWidth, maxWidth), 100);

          updateSize(newWidth);
          return;
        }

        // Diagonal resize
        // Width calculation
        if (direction === 'ne' || direction === 'se') {
          newWidth = startWidth - (e.pageX - startX);
        } else if (direction === 'nw' || direction === 'sw') {
          newWidth = startWidth + (e.pageX - startX);
        }

        // Height calculation
        if (direction === 'nw' || direction === 'ne') {
          newHeight = startHeight - (e.pageY - startY);
        } else if (direction === 'sw' || direction === 'se') {
          newHeight = startHeight + (e.pageY - startY);
        }

        newWidth = Math.max(Math.min(newWidth, maxWidth), 100);
        newHeight = Math.max(Math.min(newHeight, maxHeight), 100);

        updateSize(newWidth, newHeight);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [updateSize, editorWidth]
  );

  const proxyUrl = useMemo(() => {
    return `${
      origin === 'localhost' ? 'http' : 'https'
    }://dev.${origin}/api/pdf-proxy?url=${encodeURIComponent(fileUrl)}`;
  }, [fileUrl, origin]);

  return (
    <>
      <figure
        data-drag-handle
        draggable="true"
        className={flexWrapperClassName}
        contentEditable={false}
      >
        <div className={cn('iframe-content-wrapper group', wrapperClass)}>
          <div
            style={{
              width: mediaWidth ? `${mediaWidth}px` : 'auto',
              height: mediaHeight ? `${mediaHeight}px` : 'auto',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
            className={cn('outline-2-outline-offset-2 z-10 max-h-fit', {
              'bg-blue-dark-100 outline': selected,
              'outline-primary-500 group-hover:outline': updateSize,
            })}
            onClick={handleClick}
            ref={(el) => {
              ContentRef.current = el as HTMLDivElement;
            }}
          >
            <div data-drag-handle draggable="true">
              <div className="no-underline select-none grow-1 text-inherit">
                <div
                  className="relative flex-grow overflow-auto max-h-fit"
                  style={{
                    height: mediaHeight ? `${mediaHeight}px` : 'auto',
                  }}
                >
                  <Document
                    file={proxyUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    className="w-full"
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={docWidth}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="p-5 !bg-gray-500 flex justify-center"
                    />
                  </Document>
                </div>
              </div>
            </div>
          </div>
          {updateSize && (
            <>
              <div
                draggable="false"
                onMouseDown={(e) => handleResizeFile(e, 'left', true)}
                className="absolute top-1.5 z-10 invisible w-2 h-[calc(100%-12px)] transition-all opacity-0 -left-1 group-hover:visible group-hover:opacity-70 bg-transparent cursor-col-resize"
              ></div>
              <div
                draggable="false"
                onMouseDown={(e) => handleResizeFile(e, 'right', true)}
                className="absolute top-1.5 z-10 invisible w-2 h-[calc(100%-12px)] transition-all opacity-0 -right-1 group-hover:visible group-hover:opacity-70 bg-transparent cursor-col-resize"
              ></div>
              <div
                className={cn(
                  'absolute w-2.5 h-2.5 bg-primary-500 -left-1 -top-1 cursor-nw-resize border border-base-white rounded-sm group-hover:visible resize-rect',
                  { invisible: !selected }
                )}
                onMouseDown={(e) => handleResizeFile(e, 'ne')}
              ></div>
              <div
                className={cn(
                  'absolute w-2.5 h-2.5 bg-primary-500 -right-1 -top-1 cursor-ne-resize border border-base-white rounded-sm  group-hover:visible resize-rect',
                  { invisible: !selected }
                )}
                onMouseDown={(e) => handleResizeFile(e, 'nw')}
              ></div>
              <div
                className={cn(
                  'absolute w-2.5 h-2.5 bg-primary-500 -left-1 -bottom-1 cursor-sw-resize border border-base-white rounded-sm  group-hover:visible resize-rect',
                  { invisible: !selected }
                )}
                onMouseDown={(e) => handleResizeFile(e, 'se')}
              ></div>
              <div
                className={cn(
                  'absolute w-2.5 h-2.5 bg-primary-500 -right-1 -bottom-1 cursor-se-resize border border-base-white rounded-sm group-hover:visible resize-rect',
                  { invisible: !selected }
                )}
                onMouseDown={(e) => handleResizeFile(e, 'sw')}
              ></div>
            </>
          )}
        </div>
      </figure>
      <div className="flex justify-center">
        <ControlPanel
          numPages={numPages}
          pageNumber={pageNumber}
          scale={scale}
          isPreviewOpen={isPreviewOpen}
          toggleShowPreview={handleTogglePreview}
          handleNextClick={handleNextClick}
          handlePreviousClick={handlePreviousClick}
          downloadPdf={downloadPDF}
          zoomIn={zoomIn}
          zoomOut={zoomOut}
        />
      </div>
      {isPreviewOpen && (
        <PdfExtendedPreview
          fileUrl={proxyUrl}
          pageNumber={pageNumber}
          scale={previewScale}
          fileName={fileName}
          onClose={() => {
            setIsPreviewOpen(false);
          }}
          controlPanel={
            <ControlPanel
              numPages={numPages}
              pageNumber={pageNumber}
              scale={previewScale}
              isPreviewOpen={isPreviewOpen}
              toggleShowPreview={handleTogglePreview}
              handleNextClick={handleNextClick}
              handlePreviousClick={handlePreviousClick}
              downloadPdf={downloadPDF}
              zoomIn={zoomInPreview}
              zoomOut={zoomOutPreview}
            />
          }
        />
      )}
    </>
  );
};
